import {Error, ValidationResult, Validator} from '@/type/validation/result';
import {半角英数のみ, 必須入力, ゼロを許可する必須入力, ゼロ以上, 日付書式} from '@/type/validation/validator';
import moment from 'moment';
import {入庫入力型} from '@/type/stock/receive/unfixed';

export default class 入庫候補入力チェッカー {
  単項目チェック結果: Error[];
  相関チェック結果: Error[];

  constructor() {
    this.単項目チェック結果 = [];
    this.相関チェック結果 = [];
  }

  validate(target: 入庫入力型) {
    const 単項目チェック = new ValidationResult(入庫入力バリデータマップ一覧);
    単項目チェック.validate(target);
    this.単項目チェック結果 = 単項目チェック.getErrors;
    const 有効期限が入庫日付より過去チェック結果 = 単一行有効期限整合性チェック(target);
    const 納品データ以外の場合入庫日が未来は不可チェック結果 = 単一行納品データ以外の場合入庫日が未来は不可(target);
    this.相関チェック結果 = 有効期限が入庫日付より過去チェック結果.concat(納品データ以外の場合入庫日が未来は不可チェック結果);
  }

  validateList(target: 入庫入力型[]) {
    const 単項目チェック = new ValidationResult(入庫入力バリデータマップ一覧);
    単項目チェック.validateList(target);
    this.単項目チェック結果 = 単項目チェック.getErrors;
    const 有効期限が入庫日付より過去チェック結果 = 有効期限整合性チェック(target);
    const 納品データ以外の場合入庫日が未来は不可チェック結果 = 納品データ以外の場合入庫日が未来は不可(target);
    this.相関チェック結果 = 有効期限が入庫日付より過去チェック結果.concat(納品データ以外の場合入庫日が未来は不可チェック結果);
  }

  hasError(name: string, index: number = 0): boolean {
    return this.単項目チェック結果.concat(this.相関チェック結果).some((error) => (error.name === name && error.index === index));
  }

  hasErrors(): boolean {
    return this.単項目チェック結果.length > 0 || this.相関チェック結果.length > 0;
  }

  errorMessages(): string[] {
    const errorMEssages = this.単項目チェック結果.concat(this.相関チェック結果)
      .sort((a, b) => a.index - b.index)
      .map((value) => `${value.message}`);
    return Array.from(new Set(errorMEssages));
  }
}

export type 日付型 = Date | null;


function 入庫数は5桁以内(value: number): boolean {
  return value <= 99999;
}

function マイナス不可(value: number): boolean {
  return value > -1;
}

function 未来日時は不可(value: Date): boolean {
  if (value === null) {
    return true;
  }
  if (!日付書式(value)) {
    return true;
  }
  const momentValue = moment(value);
  const momentNow = moment(new Date());
  return momentValue.isBefore(momentNow);
}

function 入力単価は9桁以内(value: number): boolean {
  return value <= 999999999;
}

function ロット番号桁数検証(value: string): boolean {
  if (!value) {
    return true;
  }
  return value.length <= 20;
}

function 伝票番号桁数検証(value: string): boolean {
  if (!value) {
    return true;
  }
  return value.length <= 10;
}

function 伝票行番号は2桁以内(value: number): boolean {
  if (!value) {
    return true;
  }
  return value <= 99;
}

// バリデーション用のマップ
export const 入庫入力バリデータマップ一覧: Validator[] = [
  {
    name: '入庫数',
    constraints: [
      {method: 必須入力, message: '入庫数は入力必須項目です'},
      {method: マイナス不可, message: '入庫数にマイナスの値は入力できません'},
      {method: 入庫数は5桁以内, message: '入庫数は整数部5桁以内で入力してください'},
    ],
  },
  {
    name: '入力単価',
    constraints: [
      {method: ゼロを許可する必須入力, message: '単価は入力必須項目です'},
      {method: ゼロ以上, message: '単価は0以上で入力してください'},
      {method: 入力単価は9桁以内, message: '単価は整数部9桁以内で入力してください'},
    ],
  },
  {
    name: '有効期限',
    constraints: [
      {method: 必須入力, message: '有効期限は入力必須項目です'},
      {method: 日付書式, message: '有効期限の書式が不正です'},
    ],
  },
  {
    name: '入庫日付',
    constraints:  [
      {method: 必須入力, message: '入庫日付は入力必須項目です'},
      {method: 日付書式, message: '入庫日付の書式が不正です'},
    ],
  },
  {
    name: '卸コード',
    constraints:  [
      {method: 必須入力, message: '卸は入力必須項目です'},
    ],
  },
  {
    name: 'ロット番号',
    constraints: [
      {method: ロット番号桁数検証, message: 'ロット番号は20文字以下で入力してください'},
    ],
  },
  {
    name: '伝票番号',
    constraints: [
      {method: 伝票番号桁数検証, message: '伝票番号は10文字以下で入力してください'},
      {method: 半角英数のみ, message: '伝票番号は半角英数字で入力してください'},
    ],
  },
  {
    name: '伝票行番号',
    constraints: [
      {method: マイナス不可, message: '伝票行番号にマイナスの値は入力できません'},
      {method: 伝票行番号は2桁以内, message: '伝票行番号は整数部2桁以内で入力してください'},
    ],
  },
];

// methods
function 有効期限整合性チェック(入庫取込: 入庫入力型[]): Error[] {
  let errors: Error[] = [];
  入庫取込.forEach((入庫入力, index) => {
    errors = errors.concat(単一行有効期限整合性チェック(入庫入力, index));
  });
  return errors;
}

function 単一行有効期限整合性チェック(入庫入力: 入庫入力型, index: number = 0): Error[] {
  const errors: Error[] = [];
  const moment有効期限 = moment(入庫入力.有効期限);
  const moment入庫日付 = moment(入庫入力.入庫日付);
  if (moment有効期限.isBefore(moment入庫日付)) {
    errors.push({index, message: '有効期限が入庫日付より前の日付です', constraint: '', name: '有効期限'});
  }
  return errors;
}

function 納品データ以外の場合入庫日が未来は不可(target: 入庫入力型[]): Error[] {
  let errors: Error[] = [];
  target.forEach((入庫入力, index): void => {
    errors = errors.concat(単一行納品データ以外の場合入庫日が未来は不可(入庫入力, index));
  });
  return errors;
}

function 単一行納品データ以外の場合入庫日が未来は不可(入庫入力: 入庫入力型, index: number = 0): Error[] {
  const errors: Error[] = [];
  if (入庫入力.入力方法 !== '納品データ') {
    if (moment(new Date()).isBefore(moment(入庫入力.入庫日付))) {
      errors.push({index, message: '入庫日付に未来日付は入力できません', constraint: '', name: '入庫日付'});
    }
  }
  return errors;
}
